import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

interface Options<T> {
  value: T;
  onClose: () => void;
  open?: boolean;
  busy?: boolean;
}

// Compare draft values, including attachments, rather than counting input events.
// Returning every field to its original value makes the form clean again.
function snapshot(value: unknown): string {
  return JSON.stringify(value, (_key, item) => {
    if (item instanceof File) return [item.name, item.size, item.type, item.lastModified];
    if (item instanceof Set) return [...item].sort();
    return item;
  });
}

export function useUnsavedChanges<T>({ value, onClose, open = true, busy = false }: Options<T>) {
  const current = snapshot(value);
  const [baseline, setBaseline] = useState<string | null>(current);
  const [confirming, setConfirming] = useState(false);
  // Initializers call resetDirty in the same batch as loading/resetting the draft.
  // Capture the resulting render, not the stale values from the previous record.
  if (baseline === null) setBaseline(current);
  const dirty = open && baseline !== null && current !== baseline;
  const resetDirty = useCallback(() => setBaseline(null), []);
  const markSaved = () => setBaseline(current);
  // An asynchronous default must not clear edits made in other fields meanwhile.
  const acceptChanges = (patch: Partial<T> | ((saved: T) => T)) => {
    setBaseline(previous => {
      const saved = JSON.parse(previous ?? current) as T;
      return snapshot(typeof patch === 'function' ? patch(saved) : { ...saved, ...patch });
    });
  };

  useEffect(() => {
    if (!open) setConfirming(false);
  }, [open]);

  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);

  const requestClose = () => {
    if (busy) return;
    if (dirty) setConfirming(true);
    else onClose();
  };

  const dialogProps = {
    'data-form-dialog': true,
    role: 'dialog' as const,
    'aria-modal': true as const,
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // A nested dialog or a combobox gets first refusal of Escape.
      if ((event.target as HTMLElement).closest('[data-form-dialog]') !== event.currentTarget) return;
      event.preventDefault();
      event.stopPropagation();
      requestClose();
    },
  };

  const confirmation = open && confirming ? (
    <DiscardChangesDialog
      onCancel={() => setConfirming(false)}
      onDiscard={() => {
        if (busy) return;
        setConfirming(false);
        onClose();
      }}
      busy={busy}
    />
  ) : null;

  return { requestClose, confirmation, dialogProps, resetDirty, markSaved, acceptChanges };
}

function DiscardChangesDialog({ onCancel, onDiscard, busy }: {
  onCancel: () => void;
  onDiscard: () => void;
  busy: boolean;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previous = document.activeElement;
    cancelButton.current?.focus();
    const keepFocus = (event: FocusEvent) => {
      if (!panel.current?.contains(event.target as Node)) cancelButton.current?.focus();
    };
    document.addEventListener('focusin', keepFocus);
    return () => {
      document.removeEventListener('focusin', keepFocus);
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-black/50 grid place-items-center p-4"
      onClick={event => event.stopPropagation()}
      onKeyDown={event => {
        event.stopPropagation();
        if (event.key === 'Escape') {
          event.preventDefault();
          onCancel();
        }
        if (event.key === 'Tab') {
          const buttons = Array.from(panel.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []);
          const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
          const next = (index + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length;
          event.preventDefault();
          buttons[next]?.focus();
        }
      }}>
      <div ref={panel} role="alertdialog" aria-modal="true" aria-labelledby={titleId}
        aria-describedby={descriptionId} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 id={titleId} className="text-[17px] font-semibold text-ink-900">Niezapisane zmiany</h2>
        <p id={descriptionId} className="mt-3 text-[14px] text-ink-600">
          Masz niezapisane zmiany. Czy na pewno chcesz zamknąć okno i je odrzucić?
        </p>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button ref={cancelButton} type="button" onClick={onCancel} className="btn btn-secondary">
            Wróć do edycji
          </button>
          <button type="button" onClick={onDiscard} disabled={busy}
            className="btn bg-red-600 text-white hover:bg-red-700">
            Odrzuć zmiany
          </button>
        </div>
      </div>
    </div>, document.body,
  );
}
